import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal
} from "@angular/core";
import { IdentifierPoolsService } from "../identifier-pools.service";
import { DcAlertService } from "../../../ui-kit/dc-alert/dc-alert.service";
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { Identifier, IdentifierGroup, IdentifierPool } from "../identifier-pools-model";
import { MODAL_CUSTOM_DATA } from "../../../utils/create-modal-injector";
import { first } from "rxjs/operators";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { faPlusCircle, faTimes, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { IdentifierPoolsModalService } from "../identifier-pools-modal.service";
import { IdentifierPoolsValidators } from "../Identifier-pools-validators";
import { interval } from "rxjs";

@Component({
  selector: 'app-identifier-pools-add-edit-pool-modal',
  templateUrl: 'identifier-pools-add-edit-pool-modal.component.html',
  styleUrls: ['identifier-pools-add-edit-pool-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IdentifierPoolsAddEditPoolModalComponent implements OnInit, AfterViewInit {
  private readonly identifierPoolsService = inject(IdentifierPoolsService);
  private readonly alertService = inject(DcAlertService);
  private readonly ngbActiveModal = inject(NgbActiveModal);
  private readonly identifierPoolsModalService = inject(IdentifierPoolsModalService);

  protected readonly identifierPoolData = signal(inject(MODAL_CUSTOM_DATA) as IdentifierPool);
  protected readonly loading = signal<boolean>(false);
  protected readonly isEdit = signal<boolean>(false);
  protected readonly typeOptions = IDENTIFIER_TYPE_OPTIONS;
  protected readonly plusIcon = faPlusCircle;
  protected readonly uploadIcon = faUpload;
  protected readonly timesIcon = faTimes;
  protected readonly trashIcon = faTrash;

  protected form: FormGroup;

  public ngOnInit() {
    this.loading.set(true);
    this.isEdit.set(!!this.identifierPoolData());
    this.initPopulateIdentifierPoolForm();
  }

  public ngAfterViewInit() {
    interval(1000)
      .pipe(first())
      .subscribe({
        next: () => {
          this.initPopulateIdentifierPoolForm(this.identifierPoolData());
          this.loading.set(false);
        }
      })
  }

  private initPopulateIdentifierPoolForm(identifierPool?: IdentifierPool): void {
    const identifiersGroups: FormGroup[] = identifierPool?.identifierGroups?.length ?
      identifierPool.identifierGroups.map(identifierGroup => this.buildGroupForm(identifierGroup)) :
      [];

    const value: { [key: string]: AbstractControl } = {
      id: new FormControl(identifierPool?.id),
      name: new FormControl({
        value: identifierPool?.name ?? '',
        disabled: false
      }, {validators: [Validators.required]}),
      identifierGroups: new FormArray(identifiersGroups)
    };

    if (!this.form) {
      this.form = new FormGroup(value);
    } else {
      Object.entries(value).forEach(([key, control]) => {
        this.form.setControl(key, control);
      });
    }
  }

  private buildGroupForm(identifierGroup?: IdentifierGroup): FormGroup {
    const identifiers: FormGroup[] = identifierGroup?.identifiers?.length ?
      identifierGroup.identifiers.map(identifier => this.buildIdentifierForm(identifier, identifierGroup?.allocated)) :
      [];
    return new FormGroup({
      id: new FormControl(identifierGroup?.id),
      allocated: new FormControl(identifierGroup?.allocated ?? false),
      available: new FormControl(identifierGroup?.available ?? true),
      identifiers: new FormArray(identifiers, {validators: [Validators.required]})
    }, {validators: [IdentifierPoolsValidators.duplicateTypeInGroupValidator]})
  }

  private buildIdentifierForm(identifier?: Identifier, isAllocated: boolean = false): FormGroup {
    return new FormGroup({
      id: new FormControl(identifier?.id),
      type: new FormControl({
        value: identifier?.type ?? '',
        disabled: isAllocated
      }, {validators: [Validators.required]}),
      code: new FormControl({
        value: identifier?.code ?? '',
        disabled: isAllocated
      }, {validators: [Validators.required, IdentifierPoolsValidators.identifierCodeValidator]})
    })
  }

  protected identifierGroups(): FormArray {
    return this.form.get('identifierGroups') as FormArray;
  }

  protected identifiers(groupIndex: number): FormArray {
    return this.form.get(`identifierGroups.${groupIndex}.identifiers`) as FormArray;
  }

  protected addIdentifierGroup(groupIndex: number): void {
    (this.form.get('identifierGroups') as FormArray)
      .push(this.buildGroupForm());
    this.addIdentifier(groupIndex);
    const element = document.querySelector('.groups-container');
    element.scrollTo({top: element.scrollHeight, behavior: 'smooth'})
  }

  protected removeIdentifierGroup(groupIndex: number): void {
    (this.form.get('identifierGroups') as FormArray)
      .removeAt(groupIndex);
  }

  protected addIdentifier(groupIndex: number): void {
    ((this.form.get('identifierGroups') as FormArray)
      .at(groupIndex).get('identifiers') as FormArray)
      .push(this.buildIdentifierForm());
  }

  protected removeIdentifier(groupIndex: number, identifierIndex: number): void {
    ((this.form.get('identifierGroups') as FormArray)
      .at(groupIndex).get('identifiers') as FormArray)
      .removeAt(identifierIndex);
  }

  protected close(result?: string): void {
    this.ngbActiveModal.close(result ?? 'close');
  }

  protected save(): void {
    this.loading.set(true);
    const payload = this.form.value;
    const request = this.isEdit() ? this.identifierPoolsService.updateIdentifierPoolById(payload) :
      this.identifierPoolsService.createIdentifierPool((payload));
    request.pipe(
      first()
    )
      .subscribe({
        next: (result) => {
          this.close('success');
        },
        error: (error) => {
          this.alertService.showAlert({
            type: 'danger',
            message: `Failed to ${this.isEdit() ? 'update' : 'create'} identifier pool: ${error.error.errors[0]}`
          });
          console.log(error);
          this.loading.set(false);
        },
        complete: () => this.loading.set(false)
      })
  }

  protected ImportIdentifierPools(): void {
    this.identifierPoolsModalService
      .openBulkImportModal(undefined, true)
      .pipe(
        first()
      )
      .subscribe({
        next: (groups) => {
          if (groups) {
            this.alertService.showAlert({
              type: 'success',
              message: `groups imported successfully.`,
            });
            this.transformAddImportedGroups(groups);
          }
        }
      })
  }

  private transformAddImportedGroups(importedGroups: { [key: string]: string }[]): void {
    this.loading.set(true);
    importedGroups.forEach((importedGroup: { [key: string]: string }) => {
      const identifiers: Identifier[] = Object.entries(importedGroup)
        .map(([type, code]) => {
          type = IDENTIFIER_TYPE_OPTIONS.find(option => option.id.toLowerCase() === type.toLowerCase())?.id;

          if (!type) {
            this.alertService.showAlert({
              type: 'danger',
              message: `Invalid identifier type: ${type}`
            })
            throw new Error(`Invalid identifier type: ${type}`);
          }

          return {
            type,
            code
          }
        });

      const newGroup: IdentifierGroup = {
        allocated: false,
        available: true,
        identifiers: identifiers
      }

      const groupForm = this.buildGroupForm(newGroup);

      (this.form.get('identifierGroups') as FormArray).push(groupForm);
      this.loading.set(false);
    })
  }
}

const IDENTIFIER_TYPE_OPTIONS = [
  {id: 'Common Code', displayValue: 'Common Code'},
  {id: 'CUSIP', displayValue: 'CUSIP'},
  {id: 'ISIN', displayValue: 'ISIN'},
  {id: 'RIC', displayValue: 'RIC'},
  {id: 'Valoren', displayValue: 'Valoren'},
  {id: 'WKN', displayValue: 'WKN'}
];
